import { Component, ElementRef, HostBinding, Signal, computed, input, signal, viewChild } from '@angular/core';
import { BaseViewComponent, Icon, IconItem, SidebarItemState } from '@sb/library/base';
import { animateText } from '../sidebar-animations';

import { Field, Scope } from '@sb/library/base';
import { NavigationItemState } from '@sb/library/data-models';

export class SidebarScope extends Scope {
    onLinkTextChange = Field<Signal<boolean>>();
    ForceDefaultAnimation = Field<() => void>();
    CloseTheSidePanel = Field<() => void>();
}

@Component({
    selector: 'lib-sidebar-item',
    templateUrl: './sidebar-item.component.html',
    styleUrls: ['./sidebar-item.component.scss'],
    animations: [animateText]
})
export class SideBarItemComponent extends BaseViewComponent {
    Scope = this.SetScope({ Requires: SidebarScope });

    @HostBinding('attr.tabindex') readonly hostTabIndex = -1;

    readonly routerLink = input('');
    readonly navigationTitle = input('');
    readonly icon = input<IconItem>(Icon.Common.Calendar);
    readonly badgeText = input<string | number | undefined>(undefined);
    readonly fragment = input<string>();
    readonly queryParams = input<{ [key: string]: any }>();
    readonly isAction = input(false);
    readonly expanded = input<boolean | null>(null);
    readonly controls = input<string>();
    readonly visibility = input<NavigationItemState>(NavigationItemState.Show);

    /** Input signal for link active state. If initialized with Hidden, state cannot change. */
    readonly linkActive = input<SidebarItemState>(SidebarItemState.Inactive);
    readonly disableTooltip = input(false);
    /** True when the label is always rendered in full, e.g. inside the overflow panel, regardless of the sidebar's collapsed state. */
    readonly textAlwaysVisible = input(false);

    readonly labelElRef = viewChild<ElementRef<HTMLElement>>('labelEl');

    private readonly _isLabelTruncated = signal(false);
    private readonly _linkActiveState = signal<SidebarItemState | undefined>(undefined);

    /** Computed signal: true if link is active. If input linkActive is Hidden, never show active state. */
    readonly isLinkActive = computed(() => {
        // If the input linkActive was set to Hidden, never show active state
        if (this.linkActive() === SidebarItemState.Hidden) {
            return false;
        }
        // Otherwise, check the internal state
        const state = this._linkActiveState() ?? this.linkActive();
        return state === SidebarItemState.Active;
    });

    constructor(
        private _ElementRef: ElementRef
    ) {
        super();
    }

    SetLinkActiveState(state: SidebarItemState): void {
        if (this.linkActive() === SidebarItemState.Hidden) {
            return;
        }
        this._linkActiveState.set(state);
    }

    /** Get the current link active state */
    GetLinkActiveState(): SidebarItemState {
        return this._linkActiveState() ?? this.linkActive();
    }

    UpdateLabelTruncation(): void {
        const el = this.labelElRef()?.nativeElement;
        if (!el) return;
        this._isLabelTruncated.set(el.scrollWidth > el.clientWidth);
    }

    ForceDefaultAnimation(): void {
        this.Scope.ForceDefaultAnimation();
    }

    get isLabelTruncated(): boolean {
        return this._isLabelTruncated();
    }

    get hostNativeElement(): HTMLElement {
        return this._ElementRef.nativeElement;
    }
}
